import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  LinearProgress,
  CircularProgress,
  Alert
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import leaderboardService from '../services/leaderboardService';

const GroupLeaderboard = () => {
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        setLoading(true);
        const data = await leaderboardService.getMyGroupRanking();
        setRanking(data);
        setError(null);
      } catch (err) {
        setError('Unable to load group ranking');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!ranking || ranking.hasGroup === false || ranking.HasGroup === false) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">Group Ranking</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {ranking?.message || ranking?.Message || 'You are not assigned to a group yet'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getPerformanceLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'success';
      case 'Very Good': return 'info';
      case 'Good': return 'primary';
      case 'Fair': return 'warning';
      default: return 'error';
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3, overflow: 'visible' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmojiEventsIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Group Ranking
            </Typography>
          </Box>
          <Chip 
            label={ranking.performanceLevel || ranking.PerformanceLevel}
            color={getPerformanceLevelColor(ranking.performanceLevel || ranking.PerformanceLevel)}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Main Content - Horizontal Layout */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
          gap: 3,
          alignItems: 'center'
        }}>
          {/* Rank Circle */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ 
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box sx={{ 
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${
                  (ranking.rank || ranking.Rank) === 1 ? '#FFD700' :
                  (ranking.rank || ranking.Rank) === 2 ? '#C0C0C0' :
                  (ranking.rank || ranking.Rank) === 3 ? '#CD7F32' :
                  '#e3f2fd'
                })`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 3
              }}>
                <Typography variant="h2" sx={{ 
                  fontWeight: 700,
                  color: (ranking.rank || ranking.Rank) <= 3 ? '#fff' : 'primary.main'
                }}>
                  {ranking.rank || ranking.Rank}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              out of {ranking.groupSize || ranking.GroupSize} students
            </Typography>
          </Box>

          {/* Stats Grid */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 2
            }}>
              {/* Percentile */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Percentile
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {ranking.percentile || ranking.Percentile}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">%</Typography>
                </Box>
              </Box>

              {/* Avg Correct */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Avg Correct
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {Math.round(ranking.avgCorrect || ranking.AvgCorrect)}
                </Typography>
              </Box>

              {/* Completion Rate */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Completion
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {Math.round(ranking.completionRate || ranking.CompletionRate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">%</Typography>
                </Box>
              </Box>

              {/* Tests Completed */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Tests Done
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {ranking.testsCompleted || ranking.TestsCompleted}/{ranking.totalVisibleTests || ranking.TotalVisibleTests}
                </Typography>
              </Box>
            </Box>

            {/* Percentile Progress Bar */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  You performed better than {ranking.percentile || ranking.Percentile}% of your group
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={ranking.percentile || ranking.Percentile} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)',
                    borderRadius: 3
                  }
                }}
              />
            </Box>

            {/* Insights */}
            {((ranking.insights && ranking.insights.messages) || (ranking.Insights && ranking.Insights.Messages)) && ((ranking.insights?.messages || ranking.Insights?.Messages || []).length > 0) && (
              <Box sx={{ 
                bgcolor: 'action.hover',
                borderRadius: 1.5,
                p: 1.5,
                mt: 2
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 16 }} /> Quick Tips
                </Typography>
                {(ranking.insights?.messages || ranking.Insights?.Messages || []).map((msg, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary" sx={{ mb: 0.3, pl: 2.5 }}>
                    • {msg}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GroupLeaderboard;
